import {findIndex, PartialShallow, pick} from 'lodash';
import {Vue} from 'vue-property-decorator';

/**
 * merges given items into existing array of items
 *
 * THIS METHOD MODIFIES THE existingItems ARRAY IN-PLACE.
 *
 * For newItems that are present in existingItems, based on matching
 * predicateKeys, the existing object is merged via Object.assign. If the
 * newItem is not already present, it is pushed onto the existing array.
 *
 * In addition, this method can optionally sort the resulting, merged array
 * in-place. Sorting can be performed on a given key in ascending order.
 * Alternatively, you can specify a custom sort function to be passed to
 * Array.sort.
 *
 * @param existingItems
 * @param newItems
 * @param predicateKeys
 * @param sortMethod
 */
export const mergeAndAdd = <T = Record<string, unknown>>(
	existingItems: T[],
	newItems: T | T[],
	predicateKeys: (keyof T)[],
	sortMethod?: keyof T | ((a: T, b: T) => number),
): void => {
	if(!Array.isArray(newItems)) {
		newItems = [newItems];
	}

	newItems.forEach((newItem) => {
		const existingItemIndex = findIndex(existingItems, pick(newItem, predicateKeys) as PartialShallow<T>);
		if(existingItemIndex > -1) {
			Vue.set(existingItems, existingItemIndex, Object.assign({}, existingItems[existingItemIndex], newItem));
		} else {
			existingItems.push(newItem);
		}
	});

	if(typeof sortMethod === 'function') {
		existingItems.sort(sortMethod);
	} else if(typeof sortMethod !== 'undefined') {
		const sortByKey = (a: T, b: T): number => {
			const key: keyof T = sortMethod;
			if(a[key] === b[key]) {
				return 0;
			}

			if(typeof a[key] === 'number' && typeof b[key] === 'number') {
				return (a[key] as unknown as number) - (b[key] as unknown as number);
			} else {
				const aString = `${a[key]}`;
				const bString = `${b[key]}`;

				return aString.localeCompare(bString);
			}
		};

		existingItems.sort(sortByKey);
	}
};
